import { Badge, Body, Box, system, transition } from '@hover/blueprint';
import { Flex } from '@hover/blueprint/chakra';
import React, { useContext, useEffect } from 'react';

import { DOOR_ENUM_TO_NAME } from 'src/modules/designStudio/components/DesignMenu/Menu/DoorGroupMenu/index';
import { MenuHeader } from 'src/modules/designStudio/components/DesignMenu/Menu/MenuHeader';
import { selectionStyles } from 'src/modules/designStudio/components/SelectionButton';
import type {
  DoorCounts,
  DoorGroup,
} from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import { historyContext } from 'src/modules/designStudio/providers';
import { context } from 'src/modules/designStudio/providers/DesignStudioServiceProvider';
import { doorGroupIcons } from 'src/modules/designStudio/providers/ProductCatalogProvider/doors';
import { CustomIcon } from 'src/shared/components';
import { TranslationNamespace, useTranslate } from 'src/shared/hooks';
import { buttonPressed } from 'src/shared/telemetry';
import type { ButtonPressed } from 'src/shared/telemetry';

const DoorGroupButton = ({
  doorGroup,
  count,
  icon,
  onClick,
}: {
  doorGroup: DoorGroup;
  count: number;
  icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
  onClick: () => void;
}) => (
  <Flex
    alignContent="center"
    direction="column"
    flexBasis={0}
    flexGrow={0}
    flexShrink={0}
    justifyContent="center"
  >
    <system.button
      {...selectionStyles({ isSelected: false })}
      borderRadius={400}
      cursor="pointer"
      onClick={onClick}
      position="relative"
    >
      <Box
        bg="neutral.200"
        borderRadius={400}
        position="relative"
        px={500}
        py={500}
      >
        <CustomIcon
          color="neutral.900"
          icon={icon}
          iconHeight="32px"
          iconWidth="32px"
          {...transition.easing250('color')}
        />
        <Badge
          borderRadius="50%"
          bottom={200}
          colorScheme="neutral"
          fontSize={100}
          fontWeight={400}
          position="absolute"
          right={200}
        >
          {count}
        </Badge>
      </Box>
    </system.button>
    <Body
      fontSize={200}
      fontWeight={400}
      mx={0}
      my={200}
      p={0}
      textAlign="center"
      textColor="neutral.600"
    >
      {DOOR_ENUM_TO_NAME[doorGroup]}
    </Body>
  </Flex>
);

export const DoorGroupList = ({
  closeMenu,
  doors,
  setSelectedDoorGroup,
}: {
  closeMenu: () => void;
  doors: DoorCounts;
  setSelectedDoorGroup: (group: DoorGroup) => void;
}) => {
  const t = useTranslate(TranslationNamespace.DESIGN_STUDIO);
  const { historyState } = useContext(historyContext);
  const { appService } = useContext(context);
  const appSnapshot = appService.getSnapshot();

  const commonTrackingData = {
    primary_cta: false,
    job_id: historyState.jobId ?? undefined,
    org_id: appSnapshot.context.orgId,
    partner_id: appSnapshot.context.partnerId,
    user_id: appSnapshot.context.userId,
    user_email: historyState.email ?? undefined,
    page_or_screen_name: 'Design-studio',
    design_studio_mode: 'default',
    feature: 'doors',
    button_location: 'design-template-panel',
  } as ButtonPressed;

  useEffect(() => {
    buttonPressed({
      ...commonTrackingData,
      track_id: 'doors-navigation-selection-button',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <MenuHeader
        closeMenu={closeMenu}
        headerText={t('menu.doorGroups.title')}
      />
      <Body fontSize={300} fontWeight={700} lineHeight={300} my={200}>
        {t('menu.doorGroups.selectTitle')}
      </Body>
      <Flex
        columnGap={300}
        direction="row"
        flexWrap={{ base: 'nowrap', tablet: 'wrap' }}
        overflowX={{ base: 'auto', tablet: 'visible' }}
        paddingLeft={100}
        py={200}
        rowGap={100}
        w="100%"
      >
        {Object.entries(doors).map(
          entry =>
            entry[1].count > 0 && (
              <DoorGroupButton
                count={entry[1].count}
                doorGroup={entry[0] as DoorGroup}
                icon={doorGroupIcons[entry[0] as DoorGroup]}
                key={`door-group-button-${entry[0]}`}
                onClick={() => {
                  buttonPressed({
                    ...commonTrackingData,
                    track_id: 'door-product-type-selection-button',
                    button_text: `${entry[0]}`,
                  });
                  setSelectedDoorGroup(entry[0] as DoorGroup);
                }}
              />
            ),
        )}
      </Flex>
    </>
  );
};
